import {getTenantName} from 'common/HelperFunctions'
import SelectiveAiLoadingLogo from 'common/components/SelectiveAiLoadingLogo'
import Spinner from 'common/components/Spinner'
import {redirectToTenantWorkspaceIfNeeded} from 'common/helpers/authRedirect'
import {FC, ReactNode, useId, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router'
import {REDUX_SET_ACCESS_TOKEN} from 'setup/redux/Actions/SignIn/ActionTypes/ReduxActionTypes'
import {resetDefaultPassword} from 'setup/redux/Actions/SignIn/forgotPassword'

import {toAbsoluteUrl} from '_metronic/helpers'

import {
  clearMustCreatePassword,
  isPasswordAlreadySetError,
} from '../selective/mustCreatePasswordHelpers'
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULES,
  type PasswordRule,
  passwordGuidance,
} from './passwordGuidance'

type Props = {
  onSuccess: () => void
}

const CARD_MAX_WIDTH = 680
const LOADING_LOGO_SIZE = 88
// Brief success confirmation before auto-redirecting to the dashboard.
const REDIRECT_DELAY_MS = 2000
// The mobile keyboard takes a moment to open; the field is brought into view after it has.
const KEYBOARD_SETTLE_MS = 300

const GREEN = '#22c55e'
const RED = '#ef4444'
const MUTED = 'rgba(255, 255, 255, 0.6)'

// Safari/WebKit autofill override and mobile sizing: inline styles cannot carry either.
const pageStyles = `
.cpg-input:-webkit-autofill,
.cpg-input:-webkit-autofill:hover,
.cpg-input:-webkit-autofill:focus,
.cpg-input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px rgba(55, 35, 35, 0.95) inset !important;
  box-shadow: 0 0 0 1000px rgba(55, 35, 35, 0.95) inset !important;
  -webkit-text-fill-color: #ffffff !important;
  caret-color: #ffffff !important;
  border-radius: 10px !important;
  transition: background-color 5000s ease-in-out 0s !important;
}
/* Light, so the hint never reads as typed text. The input's own -webkit-text-fill-color (white)
   also paints the placeholder in Chrome/Safari, so the placeholder sets its own; opacity 1 keeps
   Firefox from dimming it further. */
.cpg-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
  -webkit-text-fill-color: rgba(255, 255, 255, 0.4);
  opacity: 1;
}
.cpg-field { border: 1.5px solid transparent; border-radius: 11px; transition: border-color 0.15s ease; }
.cpg-field:focus-within { border-color: #3b82f6; }
.cpg-field.is-invalid, .cpg-field.is-invalid:focus-within { border-color: ${RED}; }
.cpg-toggle:focus-visible { outline: 2px solid #3b82f6; outline-offset: 2px; border-radius: 4px; }
.cpg-submit:focus-visible { outline: 3px solid rgba(59, 130, 246, 0.45); outline-offset: 2px; }
@media (max-width: 575.98px) {
  .cpg-card { padding: 28px 20px !important; border-radius: 18px !important; }
  .cpg-title { font-size: 22px !important; }
  .cpg-submit { padding: 12px 16px !important; font-size: 14px !important; white-space: nowrap !important; }
}`

/**
 * Brings a field and the message under it into view once the mobile keyboard has opened (the
 * visual viewport is then shorter than the layout viewport).
 */
const revealAboveKeyboard = (el: HTMLElement | null) => {
  if (!el) return
  window.setTimeout(() => {
    const viewport = window.visualViewport
    const visibleTop = viewport ? viewport.offsetTop : 0
    const visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight
    const rect = el.getBoundingClientRect()
    if (rect.top < visibleTop + 12 || rect.bottom > visibleBottom - 12) {
      el.scrollIntoView?.({block: 'center', behavior: 'smooth'})
    }
  }, KEYBOARD_SETTLE_MS)
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(55, 35, 35, 0.8)',
  border: 'none',
  borderRadius: 10,
  padding: '13px 16px',
  // Room for the visibility toggle, on the end side in RTL too.
  paddingInlineEnd: 44,
  color: 'white',
  WebkitTextFillColor: 'white',
  fontSize: 15,
  outline: 'none',
  boxSizing: 'border-box',
}

/**
 * A password input with a visibility toggle. Two inputs, so `type` never changes on an autofilled
 * field (browsers clear autofilled values when it does); paste and password managers work as on
 * any password field.
 */
const PasswordField: FC<{
  id: string
  value: string
  onChange: (value: string) => void
  onFocus: () => void
  onBlur?: () => void
  placeholder: string
  autoComplete: string
  invalid: boolean
  describedBy?: string
  showLabel: string
  hideLabel: string
}> = ({
  id,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  autoComplete,
  invalid,
  describedBy,
  showLabel,
  hideLabel,
}) => {
  const [show, setShow] = useState(false)
  const passwordRef = useRef<HTMLInputElement>(null)
  const textRef = useRef<HTMLInputElement>(null)

  const toggle = () => {
    // Password managers fill the DOM without firing React onChange: read the shown input first.
    const active = show ? textRef.current : passwordRef.current
    if (active && active.value && active.value !== value) onChange(active.value)
    setShow((v) => !v)
  }

  const shared = {
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    onInput: (e: React.FormEvent<HTMLInputElement>) =>
      onChange((e.target as HTMLInputElement).value),
    onFocus,
    onBlur,
    placeholder,
    className: 'cpg-input',
    'aria-invalid': invalid || undefined,
    'aria-describedby': describedBy,
    spellCheck: false,
    autoCapitalize: 'off',
    autoCorrect: 'off',
  }

  return (
    <div className={`cpg-field${invalid ? ' is-invalid' : ''}`} style={{position: 'relative'}}>
      <input
        {...shared}
        ref={passwordRef}
        id={id}
        type='password'
        autoComplete={autoComplete}
        style={{...inputStyle, display: show ? 'none' : 'block'}}
      />
      <input
        {...shared}
        ref={textRef}
        id={`${id}-visible`}
        type='text'
        autoComplete='off'
        style={{...inputStyle, display: show ? 'block' : 'none'}}
      />
      <button
        type='button'
        className='cpg-toggle'
        onClick={toggle}
        aria-label={show ? hideLabel : showLabel}
        aria-pressed={show}
        style={{
          position: 'absolute',
          insetInlineEnd: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.55)',
          cursor: 'pointer',
          padding: 4,
          lineHeight: 1,
          zIndex: 10,
          WebkitAppearance: 'none',
        }}
      >
        <i
          className={show ? 'fas fa-eye-slash' : 'fas fa-eye'}
          style={{fontSize: 16}}
          aria-hidden
        />
      </button>
    </div>
  )
}

/** Focuses whichever of a field's two inputs is shown. */
const focusField = (id: string) => {
  const hidden = document.getElementById(id) as HTMLInputElement | null
  const visible = document.getElementById(`${id}-visible`) as HTMLInputElement | null
  const target = hidden && hidden.style.display !== 'none' ? hidden : visible
  target?.focus({preventScroll: true})
}

const Message: FC<{id?: string; color: string; icon?: string; children: ReactNode}> = ({
  id,
  color,
  icon,
  children,
}) => (
  <div
    id={id}
    style={{display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13, color}}
  >
    {icon && <i className={icon} style={{fontSize: 12, width: 14, flexShrink: 0}} aria-hidden />}
    <span>{children}</span>
  </div>
)

/**
 * The Da1TimeTable "Set Your Password" screen, guided (Point 1 of the journey improvements):
 * a compact rules hint on arrival, a live checklist from the first focus or keystroke, Continue
 * enabled once both fields have text — it validates and never submits an invalid password — and
 * "Passwords match" only for a valid, matching pair. Saves through the existing
 * reset-default-password call and keeps its destination.
 */
const CreatePassword: FC<Props> = ({onSuccess}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const t = (key: string, values?: Record<string, ReactNode>) =>
    intl.formatMessage({id: `AUTH.SET_PASSWORD.${key}`}, values as any)

  // Fed to a type="hidden" autocomplete="username" input so password managers (Safari
  // especially) save the new password against this account.
  const accountEmail = useSelector(
    (state: any) => state.userReducer?.user?.email || state.userReducer?.user?.username || ''
  ) as string

  const uid = useId()
  const passwordId = `${uid}-password`
  const confirmId = `${uid}-confirm`
  const hintId = `${uid}-hint`
  const checklistId = `${uid}-checklist`
  const createErrorId = `${uid}-create-error`
  const confirmMessageId = `${uid}-confirm-message`
  const continueHintId = `${uid}-continue-hint`

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  /** Create Password has focus: the checklist shows while it does, even before typing. */
  const [createFocused, setCreateFocused] = useState(false)
  /** Continue was pressed with entries that are not valid (Screen 3). */
  const [attempted, setAttempted] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saveError, setSaveError] = useState('')

  const createGroupRef = useRef<HTMLDivElement>(null)
  const confirmGroupRef = useRef<HTMLDivElement>(null)

  const guidance = passwordGuidance(password, confirmPassword, attempted)
  // The checklist replaces the compact hint while Create Password has focus or any text — so it
  // stays as the user moves on to Confirm Password — and gives way to the hint again when the
  // field is left empty.
  const showChecklist = createFocused || password.length > 0

  const changePassword = (value: string) => {
    setPassword(value)
    setSaveError('')
  }
  const changeConfirm = (value: string) => {
    setConfirmPassword(value)
    setSaveError('')
  }

  const submit = () => {
    setLoading(true)
    setSaveError('')
    dispatch(
      resetDefaultPassword(
        password,
        (response: any) => {
          // The call returns a fresh access token; the default-password token is invalidated
          // server-side, so swap it in (persisted by redux-persist) to stay signed in.
          const newAccessToken = response?.result?.accessToken
          if (newAccessToken) {
            dispatch({type: REDUX_SET_ACCESS_TOKEN, payload: newAccessToken})
          }
          setLoading(false)
          setIsSuccess(true)
          setTimeout(() => {
            // Clear the must-create-password flag, then go on as signed in (never /logout).
            onSuccess()
            const tenant = getTenantName()
            if (
              newAccessToken &&
              redirectToTenantWorkspaceIfNeeded(tenant, newAccessToken, {
                path: '/dashboard',
                searchParams: {step: '0'},
              })
            ) {
              return
            }
            navigate('/dashboard?step=0', {replace: true})
          }, REDIRECT_DELAY_MS)
        },
        (res: any) => {
          setLoading(false)
          if (isPasswordAlreadySetError(res)) {
            clearMustCreatePassword(dispatch)
            onSuccess()
            navigate('/dashboard?step=0', {replace: true})
            return
          }
          // Kept on screen with both entries: Continue tries again.
          setSaveError(res?.message || t('SAVE_FAILED'))
        }
      )
    )
  }

  const handleContinue = () => {
    if (loading || !guidance.canPressContinue) return
    if (guidance.canSubmit) {
      submit()
      return
    }
    // Never submits an invalid password: say what to correct and take the user to it.
    setAttempted(true)
    const toConfirm = guidance.firstInvalidField === 'confirm'
    focusField(toConfirm ? confirmId : passwordId)
    revealAboveKeyboard(toConfirm ? confirmGroupRef.current : createGroupRef.current)
  }

  const fixText = (fix: NonNullable<typeof guidance.createError>) =>
    t(`FIX.${fix.toUpperCase()}`, {min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH})
  const ruleLabel = (rule: PasswordRule) =>
    t(`RULE.${rule.toUpperCase()}`, {min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH})

  const createDescribedBy = [
    showChecklist ? checklistId : hintId,
    guidance.createError ? createErrorId : null,
  ]
    .filter(Boolean)
    .join(' ')

  const labelStyle: React.CSSProperties = {
    color: 'white',
    fontWeight: 500,
    fontSize: 15,
    marginBottom: 10,
    display: 'block',
  }

  if (isSuccess) {
    return (
      <Page>
        <div
          style={{
            background: 'rgba(28, 23, 33, 0.95)',
            backdropFilter: 'blur(16px)',
            borderRadius: 24,
            padding: '60px 48px',
            width: '100%',
            maxWidth: CARD_MAX_WIDTH,
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
          }}
        >
          <SelectiveAiLoadingLogo
            size={LOADING_LOGO_SIZE}
            alt={t('SUCCESS_REDIRECT')}
            className='mb-8'
            onDarkBackground
          />
          <h2 style={{color: 'white', fontSize: 30, fontWeight: 700, marginBottom: 20}}>
            {t('SUCCESS_TITLE')}
          </h2>
          <p style={{color: 'rgba(255, 255, 255, 0.7)', fontSize: 18, lineHeight: 1.6, margin: 0}}>
            {t('SUCCESS_TEXT')}
          </p>
          <p
            aria-live='polite'
            style={{marginTop: 28, marginBottom: 0, color: 'rgba(255,255,255,0.55)', fontSize: 14}}
          >
            {t('SUCCESS_REDIRECT')}
          </p>
        </div>
      </Page>
    )
  }

  return (
    <Page>
      {/* Logo (outside the card) */}
      <div style={{textAlign: 'center', marginBottom: 24}}>
        <div style={{display: 'inline-flex', alignItems: 'center', gap: 10}}>
          <img src={toAbsoluteUrl('/media/auth/image 70.png')} alt='' style={{height: 34}} />
          <span style={{color: 'white', fontWeight: 600, fontSize: 20, letterSpacing: '0.01em'}}>
            TimeTable™
          </span>
        </div>
      </div>

      <form
        className='cpg-card'
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          handleContinue()
        }}
        style={{
          background: 'rgba(40, 30, 35, 0.9)',
          backdropFilter: 'blur(16px)',
          borderRadius: 24,
          border: '1.5px solid rgba(59, 130, 246, 0.55)',
          padding: '40px 48px',
          width: '100%',
          maxWidth: CARD_MAX_WIDTH,
          boxShadow: '0 0 32px rgba(59, 130, 246, 0.12)',
        }}
      >
        <div style={{textAlign: 'center', marginBottom: 32}}>
          <h1
            className='cpg-title'
            style={{color: 'white', fontSize: 26, fontWeight: 700, margin: 0}}
          >
            {t('TITLE')}
          </h1>
          <p style={{color: MUTED, fontSize: 13, lineHeight: 1.5, marginTop: 10, marginBottom: 0}}>
            {t('SUBTITLE')}
          </p>
        </div>

        {/* For the password manager only: anchors the saved password to this account. */}
        {accountEmail && (
          <input type='hidden' name='username' autoComplete='username' value={accountEmail} />
        )}

        {/* Create Password */}
        <div ref={createGroupRef} style={{marginBottom: 24}}>
          <label htmlFor={passwordId} style={labelStyle}>
            {t('CREATE_LABEL')} <span style={{color: RED}}>*</span>
          </label>
          <PasswordField
            id={passwordId}
            value={password}
            onChange={changePassword}
            onFocus={() => {
              setCreateFocused(true)
              revealAboveKeyboard(createGroupRef.current)
            }}
            onBlur={() => setCreateFocused(false)}
            placeholder={t('CREATE_PLACEHOLDER')}
            autoComplete='new-password'
            invalid={Boolean(guidance.createError)}
            describedBy={createDescribedBy}
            showLabel={t('SHOW')}
            hideLabel={t('HIDE')}
          />

          {guidance.createError && (
            <Message id={createErrorId} color={RED}>
              {fixText(guidance.createError)}
            </Message>
          )}

          {showChecklist ? (
            <div id={checklistId} style={{marginTop: 12}}>
              <div style={{color: MUTED, fontSize: 13, marginBottom: 8}}>{t('MUST_INCLUDE')}</div>
              <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                {PASSWORD_RULES.map((rule) => {
                  const met = guidance.checks[rule]
                  // Unmet rules stay neutral until an invalid Continue marks them red.
                  const failed = !met && attempted
                  const color = met ? GREEN : failed ? RED : MUTED
                  const icon = met ? 'fas fa-check' : failed ? 'fas fa-times' : 'far fa-circle'
                  return (
                    <li
                      key={rule}
                      style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}
                    >
                      <i
                        className={icon}
                        style={{fontSize: 12, color, width: 14, flexShrink: 0}}
                        aria-hidden
                      />
                      <span style={{fontSize: 13, color}}>
                        {ruleLabel(rule)}
                        <span className='visually-hidden'>
                          {' '}
                          {met ? t('RULE_MET') : t('RULE_NOT_MET')}
                        </span>
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : (
            <div id={hintId} style={{marginTop: 8, fontSize: 13, color: MUTED}}>
              {t('HINT', {min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH})}
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div ref={confirmGroupRef}>
          <label htmlFor={confirmId} style={labelStyle}>
            {t('CONFIRM_LABEL')} <span style={{color: RED}}>*</span>
          </label>
          <PasswordField
            id={confirmId}
            value={confirmPassword}
            onChange={changeConfirm}
            onFocus={() => revealAboveKeyboard(confirmGroupRef.current)}
            placeholder={t('CONFIRM_PLACEHOLDER')}
            autoComplete='new-password'
            invalid={attempted && guidance.match === 'mismatch'}
            describedBy={guidance.match ? confirmMessageId : undefined}
            showLabel={t('SHOW')}
            hideLabel={t('HIDE')}
          />
          <div aria-live='polite'>
            {guidance.match === 'match' && (
              <Message id={confirmMessageId} color={GREEN} icon='fas fa-check'>
                {t('MATCH')}
              </Message>
            )}
            {guidance.match === 'mismatch' && (
              <Message id={confirmMessageId} color={RED}>
                {t('MISMATCH')}
              </Message>
            )}
          </div>
        </div>

        {/* Beside Continue: what to do to continue (after an invalid Continue only). */}
        <div aria-live='polite'>
          {guidance.continueHint && (
            <Message id={continueHintId} color={RED}>
              {t(`CONTINUE_HINT.${guidance.continueHint.toUpperCase()}`, {
                min: PASSWORD_MIN_LENGTH,
                max: PASSWORD_MAX_LENGTH,
              })}
            </Message>
          )}
        </div>

        {saveError && (
          <div role='alert' style={{marginTop: 14, fontSize: 13, color: RED}}>
            {saveError}
          </div>
        )}

        <button
          type='submit'
          className='cpg-submit'
          disabled={!guidance.canPressContinue || loading}
          aria-describedby={guidance.continueHint ? continueHintId : undefined}
          aria-busy={loading || undefined}
          style={{
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: 10,
            padding: '12px 28px',
            fontSize: 15,
            fontWeight: 600,
            width: '100%',
            marginTop: 20,
            cursor: guidance.canPressContinue && !loading ? 'pointer' : 'not-allowed',
            opacity: guidance.canPressContinue ? 1 : 0.55,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            transition: 'opacity 0.2s ease',
          }}
        >
          {loading ? (
            <Spinner size='sm' type='border' />
          ) : (
            <>
              {t('CONTINUE')}
              <i className='fas fa-arrow-right' style={{fontSize: 14}} aria-hidden />
            </>
          )}
        </button>
      </form>
    </Page>
  )
}

/** The full-screen backdrop both states share. */
const Page: FC<{children: ReactNode}> = ({children}) => (
  <div
    style={{
      position: 'fixed',
      // Leave room for the support/impersonation banner (App root) when present.
      top: 'var(--support-alert-height, 0px)',
      right: 0,
      bottom: 0,
      left: 0,
      background: 'linear-gradient(90.14deg, #0D0E12 20.07%, #431214 99.87%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      // Not centred with justify-content: on a short or keyboard-covered screen the top would be
      // cut off. `margin: auto 0` below centres it when there is room and scrolls otherwise.
      overflowY: 'auto',
      zIndex: 9999,
      padding: '24px 16px',
    }}
  >
    <style>{pageStyles}</style>
    <div
      style={{
        margin: 'auto 0',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {children}
    </div>
  </div>
)

export default CreatePassword
